import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  SwapCloningExecuteRequest,
  SwapCloningExecuteResponse,
  SwapCloningImpact,
  SwapCloningTableRow,
} from './swaps-cloning.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class SwapsCloningService {
  private readonly endpoint = apiUrl('SwapCloning');

  constructor(private readonly http: HttpClient) {}

  getSources(): Observable<SwapCloningTableRow[]> {
    return this.http.get<unknown>(`${this.endpoint}/sources`).pipe(
      map((response) => this.mapRows(response)),
    );
  }

  getTargets(sourceTradeId: string): Observable<SwapCloningTableRow[]> {
    return this.http.get<unknown>(`${this.endpoint}/targets`, {
      params: { sourceTradeId },
    }).pipe(
      map((response) => this.mapRows(response)),
    );
  }

  execute(payload: SwapCloningExecuteRequest): Observable<SwapCloningExecuteResponse> {
    return this.http.post<unknown>(`${this.endpoint}/execute`, payload).pipe(
      map((response) => this.normalizeExecute(response)),
    );
  }

  private mapRows(response: unknown): SwapCloningTableRow[] {
    return this.extractRows(response).map((item) => this.normalizeRow(item));
  }

  private extractRows(response: unknown): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return response.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = this.read(record, ['data', 'Data']);
      const items = this.read(record, ['items', 'Items']);
      if (Array.isArray(data)) {
        return data.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
      }
      if (Array.isArray(items)) {
        return items.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object');
      }
    }

    return [];
  }

  private normalizeExecute(response: unknown): SwapCloningExecuteResponse {
    const record = this.asRecord(response) ?? {};
    return {
      sourceTradeId: this.toText(this.read(record, ['sourceTradeId', 'SourceTradeId'])),
      targetTradeId: this.toText(this.read(record, ['targetTradeId', 'TargetTradeId'])),
      cloneType: this.toText(this.read(record, ['cloneType', 'CloneType'])),
      executedAtUtc: this.toText(this.read(record, ['executedAtUtc', 'ExecutedAtUtc'])),
      impact: this.normalizeImpact(this.read(record, ['impact', 'Impact'])),
    };
  }

  private normalizeRow(record: Record<string, unknown>): SwapCloningTableRow {
    const counterparty = this.asRecord(this.read(record, ['counterParty', 'CounterParty']));
    const receiveLeg = this.asRecord(this.read(record, ['receiveLeg', 'ReceiveLeg']));

    return {
      dealId: this.toText(this.read(record, ['dealId', 'DealId'])),
      tradeId: this.toText(this.read(record, ['tradeId', 'TradeId'])),
      rfrTradeId: this.toText(this.read(record, ['rfrTradeId', 'RfrTradeId'])),
      structureId: this.toText(this.read(record, ['structureId', 'StructureId'])),
      counterparty: this.normalizeCounterparty(counterparty),
      tradeDate: this.toText(this.read(record, ['tradeDate', 'TradeDate'])),
      settlementDate: this.toText(this.read(record, ['settlementDate', 'SettlementDate'])),
      maturityDate: this.toText(this.read(record, ['maturityDate', 'MaturityDate'])),
      payLegCurrency: this.toText(this.read(record, ['currency', 'Currency'])),
      payLegNotional: this.toNullableNumber(this.read(record, ['notional', 'Notional'])),
      receiveLegCurrency: this.toText(this.read(receiveLeg ?? {}, ['ccy', 'Ccy'])),
      receiveLegNotional: this.toNullableNumber(this.read(receiveLeg ?? {}, ['notional', 'Notional'])),
      tradeStatus: this.toText(this.read(record, ['tradeStatus', 'TradeStatus'])),
    };
  }

  private normalizeImpact(value: unknown): SwapCloningImpact {
    const record = this.asRecord(value) ?? {};
    return {
      actionsToCopy: this.toNumber(this.read(record, ['actionsToCopy', 'ActionsToCopy'])),
      feesToCopy: this.toNumber(this.read(record, ['feesToCopy', 'FeesToCopy'])),
      notesToCopy: this.toNumber(this.read(record, ['notesToCopy', 'NotesToCopy'])),
      documentsToCopy: this.toNumber(this.read(record, ['documentsToCopy', 'DocumentsToCopy'])),
      targetActionsToReplace: this.toNumber(this.read(record, ['targetActionsToReplace', 'TargetActionsToReplace'])),
      targetFeesToReplace: this.toNumber(this.read(record, ['targetFeesToReplace', 'TargetFeesToReplace'])),
      targetNotesToReplace: this.toNumber(this.read(record, ['targetNotesToReplace', 'TargetNotesToReplace'])),
      targetDocumentsToReplace: this.toNumber(this.read(record, ['targetDocumentsToReplace', 'TargetDocumentsToReplace'])),
      willTransferDocuments: this.toBoolean(this.read(record, ['willTransferDocuments', 'WillTransferDocuments'])),
      willDeleteSourceDocuments: this.toBoolean(this.read(record, ['willDeleteSourceDocuments', 'WillDeleteSourceDocuments'])),
    };
  }

  private normalizeCounterparty(counterparty: Record<string, unknown> | null): string {
    if (!counterparty) {
      return '';
    }
    const legalName = this.toText(this.read(counterparty, ['legalName', 'LegalName']));
    if (legalName) {
      return legalName;
    }
    return this.toText(this.read(counterparty, ['id', 'Id']));
  }

  private read(record: Record<string, unknown>, keys: string[]): unknown {
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(record, key)) {
        return record[key];
      }
    }
    return undefined;
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNullableNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  private toBoolean(value: unknown): boolean {
    return typeof value === 'boolean' ? value : false;
  }
}
